/**
 * External Integrations Hub schema (Task #1950).
 *
 * Persistence layer for the per-tenant + platform-scoped third-party
 * integration configurations (Stripe, WorkOS, Twilio, Resend, Slack, S3,
 * Sentry, OTel, DocuSign, Mapbox, etc.). Sensitive credential values are
 * AES-256-GCM encrypted at rest via `server/pii-crypto.ts`.
 *
 * Source of truth for behaviour:
 *   - server/services/integrations-hub/index.ts        (registry + CRUD)
 *   - server/services/integrations-hub/adapters/*.ts   (per-kind adapters)
 *   - docs/source-of-truth/engineering/22_INTEGRATIONS_HUB.md
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  timestamp,
  jsonb,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

export const integrationConfigurations = pgTable(
  "integration_configurations",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    /** Null when scope = 'platform'. Otherwise the owning tenant. */
    tenantId: text("tenant_id"),
    /** Adapter key (e.g. 'stripe', 'workos', 'twilio', 'resend', 'slack'). */
    kind: text("kind").notNull(),
    /** 'platform' or 'tenant'. */
    scope: text("scope").notNull(),
    /** Human-friendly label for the connection (e.g. "Production Stripe"). */
    label: text("label").notNull(),
    /** Master enable/disable switch. */
    enabled: boolean("enabled").notNull().default(true),
    /**
     * Encrypted credentials map: { fieldName: 'enc:v1:<base64>' }.
     * Each value is encrypted via encryptField() before write.
     */
    encryptedCredentials: jsonb("encrypted_credentials")
      .$type<Record<string, string>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    /** Non-sensitive settings (region, webhook URL, channel name, etc.). */
    settings: jsonb("settings")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    /** Last observed status: 'ok' | 'degraded' | 'down' | 'unknown'. */
    status: text("status").notNull().default("unknown"),
    statusMessage: text("status_message"),
    lastTestedAt: timestamp("last_tested_at", { withTimezone: true }),
    lastStatusAt: timestamp("last_status_at", { withTimezone: true }),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    tenantKindIdx: index("integration_configurations_tenant_kind_idx").on(
      t.tenantId,
      t.kind,
    ),
    kindScopeIdx: index("integration_configurations_kind_scope_idx").on(
      t.kind,
      t.scope,
    ),
  }),
);
export const insertIntegrationConfigurationSchema = createInsertSchema(
  integrationConfigurations,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertIntegrationConfiguration = z.infer<
  typeof insertIntegrationConfigurationSchema
>;
export type IntegrationConfiguration =
  typeof integrationConfigurations.$inferSelect;

export const integrationTestRuns = pgTable(
  "integration_test_runs",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    configurationId: uuid("configuration_id").notNull(),
    ok: boolean("ok").notNull(),
    statusCode: text("status_code"),
    message: text("message"),
    latencyMs: integer("latency_ms"),
    details: jsonb("details")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    testedBy: text("tested_by"),
    testedAt: timestamp("tested_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    configIdx: index("integration_test_runs_config_idx").on(
      t.configurationId,
      t.testedAt,
    ),
  }),
);
export const insertIntegrationTestRunSchema = createInsertSchema(
  integrationTestRuns,
).omit({ id: true, testedAt: true });
export type InsertIntegrationTestRun = z.infer<
  typeof insertIntegrationTestRunSchema
>;
export type IntegrationTestRun = typeof integrationTestRuns.$inferSelect;

export const integrationStatusSnapshots = pgTable(
  "integration_status_snapshots",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    configurationId: uuid("configuration_id").notNull(),
    status: text("status").notNull(),
    message: text("message"),
    details: jsonb("details")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    checkedAt: timestamp("checked_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    configIdx: index("integration_status_snapshots_config_idx").on(
      t.configurationId,
      t.checkedAt,
    ),
  }),
);
export const insertIntegrationStatusSnapshotSchema = createInsertSchema(
  integrationStatusSnapshots,
).omit({ id: true, checkedAt: true });
export type InsertIntegrationStatusSnapshot = z.infer<
  typeof insertIntegrationStatusSnapshotSchema
>;
export type IntegrationStatusSnapshot =
  typeof integrationStatusSnapshots.$inferSelect;
